import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(t('forgot.errors.email_required'));
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post('/auth/forgot-password', { email });
      
      setIsEmailSent(true);
      toast.success(t('forgot.toast.sent'));
      
    } catch (error) {
      console.error('Forgot password error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('forgot.errors.generic'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setIsEmailSent(false);
    onClose();
  };

  const handleBackToLogin = () => {
    handleClose();
    if (onBackToLogin) onBackToLogin();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="forgot-password-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            {t('forgot.title')}
          </DialogTitle>
        </DialogHeader>

        {!isEmailSent ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('forgot.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('forgot.email_placeholder')}
                disabled={isSubmitting}
                required
              />
              <p className="text-sm text-zinc-600">
                {t('forgot.intro')}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleBackToLogin}
                className="flex-1"
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('forgot.back_to_login')}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !email}
                className="flex-1"
              >
                {isSubmitting ? t('forgot.sending') : t('forgot.submit')}
              </Button>
            </div>
          </form>
        ) : (
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-lg font-medium">{t('forgot.sent_title')}</h3>
              <p className="text-zinc-600">
                {t('forgot.sent_body')} <strong>{email}</strong>.
              </p>
              <p className="text-sm text-zinc-500">
                {t('forgot.check_inbox')}
              </p>
            </div>

            <div className="space-y-2">
              <Button onClick={handleBackToLogin} className="w-full">
                {t('forgot.back_to_login')}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleClose}
                className="w-full"
              >
                {t('common.close')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;